import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { GetStaticPathsResult, GetStaticProps } from 'next';
import TwoColumnLayout from 'layouts/TwoColumn';
import Page from 'layouts/Page';
import { SportIcon, getSportsIconsNames } from 'lib/sports';
import { i18n } from 'lib/i18n';
import { withStaticProps } from 'lib/withStaticProps';
import { leagueApi } from 'lib/api/leagueApi';
import { addressInit, contactDataInit } from 'lib/organization';
import Hero from 'components/shared/Hero/Hero';
import ContactDetails from 'components/shared/ContactDetails/ContactDetails';
import ContactUsButton from 'components/shared/ContactUsButton/ContactUsButton';
import { css } from '@emotion/react';
import { organizationApi } from 'lib/api/organizationApi';
import SeasonBox from 'components/shared/Leagues/SeasonBox';
import { RCLeagueSeason, RCLeague } from 'types/coreEntites/Leagues';
import { Grid } from '@material-ui/core';
import RegistrationPicker from 'components/shared/Leagues/RegistrationPicker';
import { Conditional } from '../Checkout/program/step/lib/application.util';
import LongDescription from 'components/shared/longDescription/LongDescription';
import { getLeagueTagDetails } from 'lib/leagueDetails';
import { useBranding } from 'hooks/useBranding';
import { useDisclaimer } from 'hooks/useDisclaimer';

import { seasonBoxContainercss } from 'styles/utils';

import SubTitle from 'components/shared/Title/SubTitle';
import { activeSeason, futureSeason, getIsPastSeason } from 'lib/seasonUtils';
import dayjs from 'dayjs';

const heroIconCss = css`
	div#heroIcons {
		display: flex;
	}
	div#heroTags {
		margin-top: 6px;
	}
`;

const sectionCss = css`
	margin-bottom: 30px;
`;

function Leagues() {
	const router = useRouter();

	const [heroTitle, setHeroTitle] = useState<string>('');
	const [heroDescription, setHeroDescription] = useState<string>('');
	const [contactData, setContactData] = useState({ ...contactDataInit });
	const [addressInfo, setAddressInfo] = useState(addressInit);
	const [leagueMainMedia, setLeagueMainMedia] = useState<string>('');
	const [sportsIconsPaths, setSportsIconsPaths] = useState<SportIcon[]>([]);
	const [leagueSeasons, setLeagueSeasons] = useState<RCLeagueSeason[]>([]);
	const [activeRegitrationSeasons, setActiveRegitrationSeasons] = useState<RCLeagueSeason[]>([]);
	const [isLoading, setLoading] = useState<boolean>(true);
	const [currentLeague, setCurrentLeague] = useState<RCLeague>();
	const [leagueLongDescription, setLeagueLongDescription] = useState<string>('');
	const [leagueTagDetails, setLeagueTagDetails] = useState<string[]>([]);
	const { leagueId } = router.query;
	const { setBranding } = useBranding();
	const { setDisclaimer } = useDisclaimer();
	const [leagueSport, setLeagueSport] = useState<number | null>(null);

	useEffect(() => {
		(async function fetchFacilityData() {
			if (!leagueId || Array.isArray(leagueId)) {
				return;
			}

			const responseLeague = await leagueApi.getLeagueById(Number(leagueId));
			const {
				address,
				name: facilityName,
				shortDescription: facilityDescription,
				organizationId,
				mainMedia,
				sports: facilitySports,
				leagueSeasons,
				leagueDetails,
			} = responseLeague;

			const facilitySportsIconsPaths = getSportsIconsNames(facilitySports);
			setLeagueSport(facilitySports[0]);
			setSportsIconsPaths(facilitySportsIconsPaths);
			setAddressInfo(address || addressInit);
			setHeroTitle(facilityName);
			setHeroDescription(facilityDescription ?? '');
			setLeagueMainMedia(mainMedia?.url ?? '');
			const activeRegitrationSeasons = leagueSeasons.filter(leagueSeason => !getIsPastSeason(leagueSeason));
			setActiveRegitrationSeasons(activeRegitrationSeasons);
			setLeagueSeasons(leagueSeasons);
			setCurrentLeague(responseLeague);
			setLeagueLongDescription(responseLeague.description ?? '');

			if (leagueDetails) {
				setLeagueTagDetails(getLeagueTagDetails(leagueDetails));
			}

			const organizationData = await organizationApi.getOrganizationData(organizationId);
			setBranding(organizationData.brandings);
			setDisclaimer(organizationData.disclaimer);

			setContactData(organizationData);

			setLoading(false);
		})();
	}, [leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

	if (router.isFallback || isLoading || !leagueSport) {
		return <div>{i18n.loading}</div>;
	}

	return (
		<Page showHeroBg heroBgImg={leagueMainMedia} css={heroIconCss}>
			<Hero>
				<Hero.Summary
					title={heroTitle}
					address={addressInfo}
					sports={sportsIconsPaths}
					tags={leagueTagDetails}
					description={heroDescription}
				/>
				<Hero.Action>
					<Conditional show={Boolean(currentLeague)}>
						<RegistrationPicker
							seasons={activeRegitrationSeasons}
							league={currentLeague as RCLeague}
							sport={leagueSport}
						/>
					</Conditional>
				</Hero.Action>
			</Hero>

			<TwoColumnLayout>
				<TwoColumnLayout.Main>
					<TwoColumnLayout.Content>
						<LongDescription title={heroTitle} description={leagueLongDescription} />
					</TwoColumnLayout.Content>
					<TwoColumnLayout.Content>
						{/* <Catalog
              title={`${heroTitle} ${i18n.spaces.spaces}`}
              medias={mediasOfSpaces}
              showViewMore={false}
              showAccessories
            />
            {leagueSeasons.find(
              season => new Date(season.startDate) > new Date()
            ) && (
              <div css={sectionCss}>
                <Typography css={titleCss}>
                  {i18n.leagues.upcomingSeasons}
                </Typography>
                <Grid container spacing={2}>
                  {leagueSeasons
                    .filter(season => new Date(season.startDate) > new Date())
                    .map(season => (
                      <Grid key={season.id} item sm={4}>
                        <SeasonBox
                          name={season.name}
                          startDate={season.startDate}
                          endDate={season.endDate}
                          activityTimes={season.activityTimes}
                          price={season.priceEarlySingle}
                          registrationStartDate={season.registrationOpen}
                        />
                      </Grid>
                    ))}
                </Grid>
            /> */}
						{leagueSeasons.find(season => futureSeason(season)) && (
							<div css={sectionCss}>
								<SubTitle text={i18n.leagues.comingUp} />

								<Grid container spacing={2}>
									{leagueSeasons
										.filter(season => futureSeason(season))
										.map(season => (
											<Grid css={seasonBoxContainercss} key={season.id} item sm={4}>
												<SeasonBox
													id={season.id}
													leagueId={season.leagueId}
													name={season.name}
													startDate={season.startDate}
													endDate={season.endDate}
													activityTimes={season.activityTimes}
													price={season.priceEarlySingle}
													registrationStartDate={season.registrationOpen}
												/>
											</Grid>
										))}
								</Grid>
							</div>
						)}

						{leagueSeasons.find(season => activeSeason(season)) && (
							<div css={sectionCss}>
								<SubTitle text={i18n.leagues.activeSeasons} />

								<Grid container spacing={2}>
									{leagueSeasons
										.filter(season => activeSeason(season))
										.map(season => (
											<Grid css={seasonBoxContainercss} key={season.id} item sm={4}>
												<SeasonBox
													id={season.id}
													leagueId={season.leagueId}
													name={season.name}
													startDate={season.startDate}
													endDate={season.endDate}
													activityTimes={season.activityTimes}
													price={season.priceEarlySingle}
													registrationStartDate={season.registrationOpen}
												/>
											</Grid>
										))}
								</Grid>
							</div>
						)}
						{/* <Conditional show={otherLeagues.length !== 0}>
              <div css={sectionCss}>
                <Catalog
                  title={i18n.leagues.otherLeagues}
                  medias={otherLeagues}
                  showAccessories={false}
                  catalogType={rcEnums.catalogTypeEnum.PROGRAM}
                  prefixUrl={'../../'}
                />
              </div>
            </Conditional> */}
						{leagueSeasons.find(season => dayjs(season.endDate).isBefore(dayjs())) && (
							<div css={sectionCss}>
								<SubTitle text={i18n.leagues.pastSeasons} />

								<Grid container spacing={2}>
									{leagueSeasons
										.filter(season => dayjs(season.endDate).isBefore(dayjs()))
										.map(season => (
											<Grid css={seasonBoxContainercss} key={season.id} item sm={4}>
												<SeasonBox
													id={season.id}
													leagueId={season.leagueId}
													name={season.name}
													startDate={season.startDate}
													endDate={season.endDate}
												/>
											</Grid>
										))}
								</Grid>
							</div>
						)}
					</TwoColumnLayout.Content>
				</TwoColumnLayout.Main>
				<TwoColumnLayout.Sidebar>
					<ContactDetails contactData={contactData} />
					<ContactUsButton />
				</TwoColumnLayout.Sidebar>
			</TwoColumnLayout>
		</Page>
	);
}

export function getStaticPaths(): GetStaticPathsResult {
	return {
		// See `docs/project-structure.md` for URL structure details.
		paths: [
			// {
			//   params: {
			//     facilitySeoName: 'monroe-sports-center-monroe-township',
			//     facilityId: '240',
			//   },
			// },
		],
		fallback: true,
	};
}

export const getStaticProps: GetStaticProps = withStaticProps(() => {
	return {
		props: {
			message: 'Hello World!',
			metadata: {
				title: 'BondSports',
				description: 'BondSports facility landing page',
			},
		},
	};
});

export default Leagues;
